import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { fetchExternalOrderByIntegration } from "../../services/externalIntegrationClient";
import {
  EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER,
  EXTERNAL_INTEGRATION_ENDPOINT_TYPES,
} from "../../constants";
import { mergeExternalOrderIntoFormData } from "../../utils/externalIntegration";

function integrationRowKey(row) {
  return `${row._id}-${row.__legacyEndpointIndex ?? "single"}`;
}

/**
 * @param {object} props
 * @param {object} props.formData
 * @param {(merged: object) => void} props.onImported
 * @param {Array} props.integrations — fetch_order_by_id rows (non-empty when rendered)
 */
export default function ExternalOrderImportPanel({
  formData,
  onImported,
  integrations,
}) {
  const toast = useToast();
  const [integrationKey, setIntegrationKey] = useState("");
  const [externalOrderId, setExternalOrderId] = useState("");
  const [fetching, setFetching] = useState(false);

  if (!integrations || integrations.length === 0) return null;

  const selected = integrations.find(
    (i) => integrationRowKey(i) === integrationKey
  );

  const handleFetch = async () => {
    if (!selected) {
      toast({
        description: "Select an integration.",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    if (!externalOrderId.trim()) {
      toast({
        description: `Enter the external order id (replaces ${EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER} in the URL).`,
        status: "warning",
        isClosable: true,
      });
      return;
    }
    setFetching(true);
    try {
      const data = await fetchExternalOrderByIntegration(
        selected,
        externalOrderId.trim()
      );
      const merged = mergeExternalOrderIntoFormData(formData, data);
      const extId = externalOrderId.trim();
      onImported({ ...merged, refId: extId });
      toast({
        description: "Order data loaded from your integration.",
        status: "success",
        isClosable: true,
      });
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.message ||
        "Could not fetch (check URL, CORS, and response shape).";
      toast({
        description: msg,
        status: "error",
        isClosable: true,
      });
    } finally {
      setFetching(false);
    }
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="primary.main"
      borderRadius="lg"
      p={4}
      mb={4}
      bg="orange.50"
    >
      <Text fontWeight="semibold" fontSize="sm" mb={1} color="gray.800">
        Fetch from external system
      </Text>
      <Text fontSize="xs" color="gray.600" mb={3}>
        Choose your integration, enter the external order id, then load fields
        below. You can edit everything before continuing.
      </Text>
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="sm" mb={1}>
            Integration (
            {EXTERNAL_INTEGRATION_ENDPOINT_TYPES.fetch_order_by_id})
          </FormLabel>
          <Select
            placeholder="Select integration…"
            value={integrationKey}
            onChange={(e) => setIntegrationKey(e.target.value)}
            bg="white"
            borderRadius="lg"
          >
            {integrations.map((row) => (
              <option
                key={integrationRowKey(row)}
                value={integrationRowKey(row)}
              >
                {row.name || "Unnamed"}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm" mb={1}>
            External order id
          </FormLabel>
          <Input
            placeholder={`Value for ${EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER} in the URL`}
            value={externalOrderId}
            onChange={(e) => setExternalOrderId(e.target.value)}
            bg="white"
            borderRadius="lg"
          />
        </FormControl>
        <HStack justify="flex-end">
          <Button
            size="sm"
            bgColor="black"
            color="white"
            _hover={{ bg: "gray.800" }}
            onClick={handleFetch}
            isLoading={fetching}
            loadingText="Fetching…"
          >
            Fetch &amp; fill form
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
